import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, InboxIcon, CrownIcon, BriefcaseIcon, ArrowRightIcon, LayoutGridIcon, UsersIcon, Share2Icon } from 'lucide-react';
import { useBusiness, getHubTabs, BUSINESS_TYPE_LABEL } from '../components/BusinessContext';
import { BusinessDashboard } from '../components/BusinessDashboard';
import { BusinessCatalog } from '../components/BusinessCatalog';
import { BusinessLeads } from '../components/BusinessLeads';
import { BusinessSettings } from '../components/BusinessSettings';
import { BusinessAIAgents } from '../components/BusinessAIAgents';
import { UpgradePlanModal } from '../components/UpgradePlanModal';
export function BusinessHub() {
  const {
    hub,
    activateHub,
    markHubSeen,
    trialActive,
    trialDaysRemaining,
    trialPlan,
    subscribed
  } = useBusiness();
  const tabs = getHubTabs(hub?.type ?? 'generic');
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  useEffect(() => {
    markHubSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!hub?.active) {
    return <ActivateHubScreen onActivate={() => activateHub('rental')} data-id="element-3304" data-mp="3nvknefizeonw" />;
  }
  const businessName = hub.name || 'Your Business';
  return <div className="flex-1 flex flex-col h-[calc(100vh-57px)] lg:h-[calc(100vh-80px)] bg-[#F8FAFC]" data-id="element-3305" data-mp="3ixridptf00p7">
      {/* Hub Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-100 px-4 lg:px-8 pt-6" data-id="element-3306" data-mp="2v7f29ro0mxnj">
        <div className="max-w-6xl mx-auto w-full" data-id="element-3307" data-mp="2q9ncp4aufnmi">
          <div className="flex items-center justify-between gap-3 mb-5 lg:pr-52" data-id="element-3308" data-mp="34wowpe6c0set">
            <div className="flex items-center gap-3" data-id="element-3309" data-mp="3068hboiyawpt">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center shadow-md" data-id="element-3310" data-mp="305gm4et9n102">
                <SparklesIcon className="w-6 h-6 text-white" data-id="element-3311" data-mp="27hkptb7lzb3t" />
              </div>
              <div data-id="element-3312" data-mp="27fqg6t1sf5f1">
                <div className="flex items-center gap-2" data-id="element-3313" data-mp="beoctrw9s873">
                  <h1 className="font-display font-bold text-xl text-dark-navy leading-tight" data-id="element-3314" data-mp="be696a7a2rp5">
                    {businessName}
                  </h1>
                  {subscribed && <button onClick={() => setUpgradeOpen(true)} title="Manage plan" className="group inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400/15 to-amber-500/10 border border-amber-400/40 pl-1.5 pr-2 py-0.5 transition-colors hover:from-amber-400/25 hover:to-amber-500/20" data-id="element-3315" data-mp="l4rhipcr7al9">
                      <CrownIcon className="w-3 h-3 text-amber-500 fill-amber-400/70" data-id="element-3316" data-mp="1ep4nw3rijur" />
                      <span className="text-[11px] font-bold tracking-wide text-amber-700 leading-none" data-id="element-3317" data-mp="6g4q3p9uqsdo">
                        {trialPlan || 'Premium'}
                      </span>
                    </button>}
                </div>
                <p className="text-sm text-slate-500" data-id="element-3318" data-mp="1ryfhcmb7esqv">
                  {BUSINESS_TYPE_LABEL[hub.type]} · Business Hub
                </p>
              </div>
            </div>

            {trialActive && !subscribed && <div className="flex items-center gap-2 bg-primary-indigo/5 border border-primary-indigo/10 rounded-xl pl-3 pr-1.5 py-1.5" data-id="element-3319" data-mp="1i83yzuqrwh0g">
                <div className="w-8 h-8 rounded-lg bg-primary-indigo/10 flex items-center justify-center flex-shrink-0" data-id="element-3320" data-mp="1e6irkox0cuxn">
                  <SparklesIcon className="w-4 h-4 text-primary-indigo" data-id="element-3321" data-mp="195l0fmu0sqk0" />
                </div>
                <div className="text-right leading-tight" data-id="element-3322" data-mp="14axmghwn7ejo">
                  <div className="text-sm font-bold text-dark-navy" data-id="element-3323" data-mp="zat22gsm5zo6">
                    {trialPlan ? `${trialPlan} Plan` : 'Free Trial'}
                  </div>
                  <div className="text-xs text-primary-indigo font-medium" data-id="element-3324" data-mp="2tnixj2ymrgxo">
                    {trialDaysRemaining} free days left
                  </div>
                </div>
                <button onClick={() => setUpgradeOpen(true)} className="ml-1 px-3 py-1.5 bg-primary-indigo text-white text-xs font-bold rounded-lg hover:bg-indigo-700 transition-colors" data-id="element-3325" data-mp="2pk35yl1f46ru">
                  Upgrade
                </button>
              </div>}
          </div>

          {/* Top Tab Navigation */}
          <div className="flex gap-1 overflow-x-auto no-scrollbar -mb-px" data-id="element-3326" data-mp="2ropzb0iosv3m">
            {tabs.map(tab => {
            const isActive = tab === activeTab;
            return <button key={tab} onClick={() => setActiveTab(tab)} className={`relative px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${isActive ? 'text-primary-indigo' : 'text-slate-500 hover:text-slate-800'}`} data-id="element-3327" data-mp="2iqebf697gbev">
                  {tab}
                  {isActive && <motion.div layoutId="hubTabIndicator" className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary-indigo rounded-full" data-id="element-3328" data-mp="1zyv8t0j3ezdd" />}
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* Trial Banner — only while still on the free trial, not after upgrading */}
      {hub?.active && trialActive && !subscribed && <div className="bg-primary-indigo/5 border-b border-primary-indigo/10 px-4 lg:px-8 py-2.5 flex items-center justify-center gap-4 text-sm" data-id="element-3329" data-mp="1lb4nq0a78hb0">
          <span className="font-bold text-primary-indigo flex items-center gap-1.5" data-id="element-3330" data-mp="1udmbij5i5i04">
            <SparklesIcon className="w-4 h-4" data-id="element-3331" data-mp="291qwxuvxw7ml" /> Trial Active
          </span>
          <span className="text-slate-400" data-id="element-3332" data-mp="293tju61spkn3">•</span>
          <span className="font-medium text-slate-700" data-id="element-3333" data-mp="243ys60a74r0z">
            {trialDaysRemaining} Days Remaining
          </span>
          <span className="text-slate-400" data-id="element-3334" data-mp="2i386svir7d2u">•</span>
          <span className="text-slate-600 hidden sm:inline" data-id="element-3335" data-mp="2d3vngkr3co50">
            Publish Listings
          </span>
          <span className="text-slate-400 hidden sm:inline" data-id="element-3336" data-mp="1aqpmxn7fvj2p">•</span>
          <span className="text-slate-600 hidden sm:inline" data-id="element-3337" data-mp="1aocxyq39k8g5">Receive Leads</span>
          <span className="text-slate-400 hidden sm:inline" data-id="element-3338" data-mp="1fpipikl6tpo3">•</span>
          <button className="font-bold text-primary-indigo hover:underline" data-id="element-3339" data-mp="1jpt6esaiq0hq">
            Upgrade Anytime
          </button>
        </div>}

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 lg:px-8 py-8" data-id="element-3340" data-mp="1tgxlrt43vnd3">
        <div className="max-w-6xl mx-auto w-full h-full" data-id="element-3341" data-mp="1tgfdtz82zkpc">
          {activeTab === 'Dashboard' ? <BusinessDashboard onNavigate={setActiveTab} data-id="element-3342" data-mp="1yhv3y5p48hel" /> : activeTab === tabs[1] ? <BusinessCatalog data-id="element-3343" data-mp="23cqut2im8yq4" /> : activeTab === 'Leads' ? <BusinessLeads data-id="element-3344" data-mp="27f5fzt9rcakk" /> : activeTab === 'AI Agents' ? <BusinessAIAgents data-id="element-3345" data-mp="91c7stgzpde2" /> : activeTab === 'Settings' ? <BusinessSettings data-id="element-3346" data-mp="d2flpafh8zcn" /> : <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} className="flex flex-col items-center justify-center text-center py-24" data-id="element-3347" data-mp="d3741jk2d0kz">
              <div className="w-16 h-16 rounded-2xl bg-white border border-slate-200 flex items-center justify-center mb-5 shadow-sm" data-id="element-3348" data-mp="3rw4rvc2lmaqe">
                <InboxIcon className="w-8 h-8 text-slate-300" data-id="element-3349" data-mp="oi0qanym1x2p" />
              </div>
              <h2 className="font-display font-bold text-2xl text-dark-navy mb-2" data-id="element-3350" data-mp="jixkh1at3mto">
                No {activeTab.toLowerCase()} yet
              </h2>
              <p className="text-slate-500 max-w-md" data-id="element-3351" data-mp="ffj2w677yye4">
                This is where your {activeTab.toLowerCase()} will appear once
                you set up your Business Hub. Ask Talab to help you get started.
              </p>
            </motion.div>}
        </div>
      </div>

      {upgradeOpen && <UpgradePlanModal onClose={() => setUpgradeOpen(false)} data-id="element-3352" data-mp="2vabbtl5xwqy5" />}
    </div>;
}
const HUB_PERKS = [{
  icon: LayoutGridIcon,
  title: 'Unified catalogue',
  desc: 'Manage every listing, publish to Talab, and stay live on your own widget.'
}, {
  icon: Share2Icon,
  title: 'Your own shop link',
  desc: 'Get a shareable shop link to send customers — your storefront, your brand.'
}, {
  icon: UsersIcon,
  title: 'Leads & team',
  desc: 'Capture every inquiry, track intent, and bring your team into one workspace.'
}];
function ActivateHubScreen({
  onActivate
}: {
  onActivate: () => void;
}) {
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-y-auto" data-id="element-3353" data-mp="186jvf2mlr7ay">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="absolute inset-0 bg-dark-navy/55 backdrop-blur-sm" data-id="element-3354" data-mp="1d438w0bh7g1u" />
      <div className="relative w-full max-w-2xl my-auto" data-id="element-3355" data-mp="25lfkr0e9cv4f">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 280,
        damping: 26
      }} className="relative bg-white rounded-3xl border border-slate-200/70 shadow-2xl overflow-hidden" data-id="element-3356" data-mp="25mz3cxndzi9d">
          {/* Soft brand glow */}
          <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-primary-indigo/10 blur-3xl" data-id="element-3357" data-mp="37et8umwj7omu" />
          <div className="pointer-events-none absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-ai-purple/10 blur-3xl" data-id="element-3358" data-mp="3gd597gn7a0sh" />

          <div className="relative px-7 lg:px-12 py-12 text-center" data-id="element-3359" data-mp="3bd93ncyuu1at">
            <motion.div initial={{
            scale: 0.8,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: 0.1,
            type: 'spring',
            stiffness: 200,
            damping: 16
          }} className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-indigo to-ai-purple shadow-lg shadow-primary-indigo/30 mb-6" data-id="element-3360" data-mp="vmwahhdadgj4">
              <BriefcaseIcon className="w-8 h-8 text-white" data-id="element-3361" data-mp="3opejqy31xql6" />
            </motion.div>

            <span className="inline-flex items-center gap-1.5 px-3 py-1 mb-4 rounded-full bg-primary-indigo/5 border border-primary-indigo/10 text-[11px] font-bold uppercase tracking-wider text-primary-indigo" data-id="element-3362" data-mp="3jtfmsm3nr095">
              <SparklesIcon className="w-3.5 h-3.5" data-id="element-3363" data-mp="39vrt7zhsfysk" /> Business Hub
            </span>

            <h1 className="font-display font-bold text-2xl lg:text-3xl text-dark-navy mb-3 leading-tight" data-id="element-3364" data-mp="35u6c21y80yck">
              Turn Talab into your selling workspace
            </h1>
            <p className="text-slate-500 max-w-md mx-auto leading-relaxed mb-9" data-id="element-3365" data-mp="142bc2jlbuia6">
              Activate your Business Hub to publish listings, capture leads, and
              get your own shareable shop link to send to customers — all in one
              place.
            </p>

            {/* Standout CTA */}
            <div className="flex flex-col items-center gap-3 mb-10" data-id="element-3366" data-mp="2vp814f9tf92f">
              <motion.button onClick={onActivate} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="group relative inline-flex items-center gap-2.5 px-8 py-4 rounded-2xl bg-gradient-to-r from-primary-indigo to-ai-purple text-white font-semibold text-base shadow-xl shadow-primary-indigo/30 overflow-hidden" data-id="element-3367" data-mp="2zoymg3gkowzk">
                {/* animated shine */}
                <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/25 to-transparent" data-id="element-3368" data-mp="3net2etw6v1o1" />
                <SparklesIcon className="w-5 h-5 relative" data-id="element-3369" data-mp="1sammnhc1jq8f" />
                <span className="relative" data-id="element-3370" data-mp="1obm47zxh6ply">Activate Business Hub</span>
                <ArrowRightIcon className="w-5 h-5 relative transition-transform group-hover:translate-x-0.5" data-id="element-3371" data-mp="22alwrio3zone" />
              </motion.button>
              <p className="text-xs text-slate-400" data-id="element-3372" data-mp="1xcu2wj3wlsgk">
                Free to start · No credit card required
              </p>
            </div>

            {/* Perks */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-left" data-id="element-3373" data-mp="14nddzvfhstp3">
              {HUB_PERKS.map((perk, i) => {
              const Icon = perk.icon;
              return <motion.div key={perk.title} initial={{
                opacity: 0,
                y: 12
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.2 + i * 0.08
              }} className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4" data-id="element-3374" data-mp="1elazib5ywce0" data-mpi={i}>
                    <div className="w-9 h-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center mb-3 shadow-sm" data-id="element-3375" data-mp="2ojkhfzyf97qg">
                      <Icon className="w-4.5 h-4.5 text-primary-indigo" data-id="element-3376" data-mp="2sl5pn800gxub" />
                    </div>
                    <h3 className="font-bold text-sm text-dark-navy mb-1" data-id="element-3377" data-mp="3gaqbbynju97a">
                      {perk.title}
                    </h3>
                    <p className="text-xs text-slate-500 leading-relaxed" data-id="element-3378" data-mp="379irglls5awq">
                      {perk.desc}
                    </p>
                  </motion.div>;
            })}
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}